import { readCache, writeCache } from './cacheStore';

const routeLoaders = {
  '/': () => import('./App'),
  '/categorizer': () => import('./Categorizer'),
  '/all-transactions': () => import('./AllTransactions'),
  '/cashflow': () => process.env.REACT_APP_CASHFLOW_V2_ENABLED === 'true' ? import('./CashFlowV2') : import('./CashFlow'),
  '/networth': () => import('./NetWorth'),
  '/transaction': () => import('./TransactionEdit'),
};

export const preloadRoute = path => (routeLoaders[path] || routeLoaders[path.startsWith('/transaction/') ? '/transaction' : ''])?.();

let prefetchPromise = null;

const prefetchTransactionViews = async () => {
  const store = await import('./dataStore');
  const defaultQuery = { search: '', category: '', owner: '', account: '', month: '', limit: 50, offset: 0, orderBy: 'date', orderDirection: 'desc' };
  const [transactions, months, review, listing, options] = await Promise.all([
    store.getTransactions(),
    store.getAllMonths(),
    store.getUncategorizedTransactions(null, 100),
    store.searchTransactions(defaultQuery),
    store.getFilterOptions(),
  ]);
  writeCache('transactions', transactions);
  writeCache('transaction-months', months);
  writeCache('categorizer-review:all', review);
  writeCache(`all-transactions:${JSON.stringify(defaultQuery)}`, listing);
  writeCache('all-transactions-filters', { options, months });
};

const prefetchCashflow = async () => {
  if (process.env.REACT_APP_CASHFLOW_V2_ENABLED !== 'true') return;
  const store = await import('./cashFlowV2Store');
  await store.getCashflowV2();
};

const prefetchNetWorth = async () => {
  if (readCache('net-worth-page')) return;
  const [store, api] = await Promise.all([import('./netWorthStore'), import('./netWorthApi')]);
  const tickerSymbols = ['ETH', 'BTC', 'SOL', 'AAVE', 'COW', 'ENS', 'SKY', 'UNI', 'LINK', 'PENDLE', 'GNO'];
  const marketRequest = Promise.all([
    api.fetchCryptoPriceChanges(tickerSymbols),
    api.fetchStockPrices(['^GSPC', '^IXIC', 'QQQ', '^TNX']),
    api.fetchDefiYields(),
    api.fetchHyperliquidPrice('BRENTOIL'),
  ]);
  const [wallets, tradfiAccounts, manualCrypto, taxReserves, snapshots] = await Promise.all([
    store.getActiveWallets(), store.getActiveTradFiAccounts(), store.getManualCrypto(), store.getTaxReserves(), store.getSnapshots(),
  ]);
  const [liveData, [cryptoPriceChanges, marketIndices, defiYields, brentPrice]] = await Promise.all([
    api.fetchAllNetWorthData(wallets, manualCrypto, tradfiAccounts), marketRequest,
  ]);
  writeCache('net-worth-page', {
    wallets, tradfiAccounts, manualCrypto, taxReserves, snapshots,
    evmWalletData: liveData.evmWallets, solanaWalletData: liveData.solanaWallets,
    stockPrices: liveData.stockPrices, cryptoPrices: liveData.cryptoPrices,
    topCryptoPrices: liveData.cryptoPrices, cryptoPriceChanges, marketIndices,
    defiYields, stUsds: defiYields?.stUsds || null, brentPrice,
    lastUpdated: liveData.lastUpdated,
  });
};

export const prefetchDashboardData = () => {
  if (prefetchPromise) return prefetchPromise;
  prefetchPromise = Promise.allSettled([
    ...Object.values(routeLoaders).map(load => load()),
    prefetchTransactionViews(),
    prefetchCashflow(),
    prefetchNetWorth(),
  ]);
  return prefetchPromise;
};
